import { useState } from "react";

import { SpecificationNotifyRecipientsEditor } from "entity/specification/common/SpecificationNotifyRecipientsEditor";
import { SpecificationShare } from "entity/specification/common/SpecificationShare";
import { SpecificationShareModalShell } from "entity/specification/common/SpecificationShareModalShell";
import {
    SpecificationOwnerShareMode,
    SpecificationSubShareMode,
} from "entity/specification/Specification.api.types";

interface ISpecificationPublishModalProps {
    visible: boolean;
    onClose: () => void;
    onPublish: (
        shareWithClients: boolean,
        shareWithSubs: boolean,
        usersToNotify: number[],
    ) => Promise<void>;
    isSubmitting: boolean;
    jobId: number;
    /**
     * When provided, the modal seeds its checkboxes from the spec's current share modes instead
     * of resetting to "shared with everyone" - used when republishing an already-published spec,
     * where visibility must not change, only the notify-recipient picker is being surfaced.
     */
    initialOwnerShareMode?: SpecificationOwnerShareMode;
    initialSubShareMode?: SpecificationSubShareMode;
    /**
     * Reuses this modal for the "Viewing permissions" action (share-mode-only update, no version
     * bump) instead of the default "Publish" action. Only the title/message/button text and
     * onPublish's semantics change - both flows show identical Clients/Subs + notify-recipients UI.
     */
    mode: "publish" | "share";
}

function SpecificationPublishModal({
    visible,
    onClose,
    onPublish,
    isSubmitting,
    jobId,
    initialOwnerShareMode,
    initialSubShareMode,
    mode,
}: ISpecificationPublishModalProps) {
    const isRepublish = mode === "publish" && initialOwnerShareMode !== undefined;
    const [ownerShareMode, setOwnerShareMode] = useState<SpecificationOwnerShareMode>(
        initialOwnerShareMode ?? SpecificationOwnerShareMode.SharedWithAllOwners,
    );
    const [subShareMode, setSubShareMode] = useState<SpecificationSubShareMode>(
        initialSubShareMode ?? SpecificationSubShareMode.SharedWithAllSubs,
    );
    const [usersToNotify, setUsersToNotify] = useState<number[]>([]);

    const handlePublish = async () => {
        const shareWithClients = ownerShareMode === SpecificationOwnerShareMode.SharedWithAllOwners;
        const shareWithSubs = subShareMode === SpecificationSubShareMode.SharedWithAllSubs;
        await onPublish(shareWithClients, shareWithSubs, usersToNotify);
    };

    const handleClose = () => {
        onClose();
    };

    const handleOwnerChange = (_field: string, value: SpecificationOwnerShareMode) => {
        setOwnerShareMode(value);
    };

    const handleSubChange = (_field: string, value: SpecificationSubShareMode) => {
        setSubShareMode(value);
    };

    const isSubmitDisabled =
        ownerShareMode === SpecificationOwnerShareMode.NotShared &&
        subShareMode === SpecificationSubShareMode.NotShared;

    // In "share" mode, a login type that already had viewing access before this change gains
    // nothing from the update, so offering to notify them would be a no-op.
    const ownersAlreadyHadAccess =
        mode === "share" &&
        initialOwnerShareMode === SpecificationOwnerShareMode.SharedWithAllOwners;
    const subsAlreadyHadAccess =
        mode === "share" && initialSubShareMode === SpecificationSubShareMode.SharedWithAllSubs;

    const title = mode === "share" ? "Viewing permissions" : "Publish spec";
    const message =
        mode === "share"
            ? "Who would you like to be able to view this Specification?"
            : isRepublish
              ? "Publishing this specification will create a new version. Would you like to notify anyone about this update?"
              : "Publishing this specification will create a new version and share it with chosen user groups";
    const testId =
        mode === "share" ? "specificationViewingPermissionModal" : "specificationPublishModal";

    return (
        <SpecificationShareModalShell
            testid={testId}
            visible={visible}
            onClose={handleClose}
            title={title}
            subtitle={message}
            confirmText={mode === "publish" ? "Publish" : undefined}
            isSubmitting={isSubmitting}
            confirmDisabled={isSubmitDisabled}
            onConfirm={handlePublish}
        >
            <SpecificationShare
                specificationsText="this specification"
                notifyOwnerValue={ownerShareMode}
                notifySubValue={subShareMode}
                notifyOwnerId="ownerShareMode"
                notifySubId="subShareMode"
                onOwnerChange={handleOwnerChange}
                onSubChange={handleSubChange}
            />
            <div className="flex flex-col gap-2 py-1">
                <SpecificationNotifyRecipientsEditor
                    jobId={jobId}
                    // "share" mode only changes client/sub viewing permissions - internal
                    // users already have access, so they aren't offered here. Backend mirrors
                    // this: HandleSharingPermissionsChangeAsync always passes includeBuilders: false.
                    includeBuilders={mode !== "share"}
                    includeClients={
                        ownerShareMode === SpecificationOwnerShareMode.SharedWithAllOwners &&
                        !ownersAlreadyHadAccess
                    }
                    includeSubs={
                        subShareMode === SpecificationSubShareMode.SharedWithAllSubs &&
                        !subsAlreadyHadAccess
                    }
                    onSelectionChange={setUsersToNotify}
                />
            </div>
        </SpecificationShareModalShell>
    );
}

export default SpecificationPublishModal;
